import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from '@/components/ui/command';
import { api } from '@/lib/api';
import { Calendar, Plane, User, Building2 } from 'lucide-react';
import { fmtDate } from '@/lib/dates';

export default function QuickSearch({ open, onOpenChange }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState({ pejabat: [], opd: [], agendas: [], sppds: [] });
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        onOpenChange(true);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onOpenChange]);

  useEffect(() => {
    if (!q || q.length < 2) {
      setResults({ pejabat: [], opd: [], agendas: [], sppds: [] });
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`);
        setResults(data);
      } catch (e) {}
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const go = (path) => {
    onOpenChange(false);
    navigate(path);
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        data-testid="quick-search-input"
        placeholder="Cari pejabat, OPD, agenda, SPPD…"
        value={q}
        onValueChange={setQ}
      />
      <CommandList>
        {q.length < 2 && (
          <div className="py-6 text-center text-sm text-slate-500">Ketik minimal 2 karakter…</div>
        )}
        {q.length >= 2 && results.pejabat.length === 0 && results.opd.length === 0 && results.agendas.length === 0 && results.sppds.length === 0 && (
          <CommandEmpty>Tidak ada hasil ditemukan untuk "{q}".</CommandEmpty>
        )}
        {results.pejabat?.length > 0 && (
          <CommandGroup heading="Pejabat">
            {results.pejabat.map((p) => (
              <CommandItem key={p.id} onSelect={() => go(`/pencarian?q=${encodeURIComponent(p.nama)}`)} value={`pejabat-${p.nama}`}>
                <User className="w-4 h-4 mr-2 text-slate-400" />
                <div className="flex-1">
                  <div className="font-semibold">{p.nama}</div>
                  <div className="text-xs text-slate-500">{p.jabatan} · {p.status_dinas === 'Sedang Dinas' ? `Sedang Dinas di ${p.lokasi_dinas}` : 'Di Kantor'}</div>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {results.opd?.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="OPD">
              {results.opd.map((o) => (
                <CommandItem key={o.id} onSelect={() => go(`/pencarian?q=${encodeURIComponent(o.nama)}`)} value={`opd-${o.kode}`}>
                  <Building2 className="w-4 h-4 mr-2 text-slate-400" />
                  <div>
                    <div className="font-semibold">{o.nama}</div>
                    <div className="text-xs text-slate-500">{o.kode} · {o.jenis}</div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
        {results.agendas?.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Agenda">
              {results.agendas.map((a) => (
                <CommandItem key={a.id} onSelect={() => go(`/agenda/${a.id}`)} value={`agenda-${a.nama_acara}`}>
                  <Calendar className="w-4 h-4 mr-2 text-slate-400" />
                  <div>
                    <div className="font-semibold">{a.nama_acara}</div>
                    <div className="text-xs text-slate-500">{fmtDate(a.tanggal_mulai)} · {a.lokasi}</div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
        {results.sppds?.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="SPPD">
              {results.sppds.map((s) => (
                <CommandItem key={s.id} onSelect={() => go(`/sppd/${s.id}`)} value={`sppd-${s.nomor_sppd}`}>
                  <Plane className="w-4 h-4 mr-2 text-slate-400" />
                  <div>
                    <div className="font-semibold">{s.nomor_sppd}</div>
                    <div className="text-xs text-slate-500">{s.pejabat_nama} → {s.tujuan_kota} · {s.status}</div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}
